import { useEffect, useState } from "react";
import { Building2, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

const KANBAN = [
  { key: "NEW", label: "New", bg: "var(--blue-light)", color: "var(--blue-dark)" },
  { key: "ACKNOWLEDGED", label: "Acknowledged", bg: "#fef3c7", color: "#92400e" },
  { key: "QUOTE_SENT", label: "Quote sent", bg: "#dcfce7", color: "#166534" },
  { key: "WON", label: "Won / Lost", bg: "#e2e8f0", color: "#475569" },
];

/** Broker / IT SP cockpit — kanban of open leads + broker-specific renewals card.
 *  isBroker=true adds the insurance-renewals card below the kanban.  */
export default function SPCockpit({ isBroker = false }) {
  const [leads, setLeads] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/sp/leads");
        if (!mounted) return;
        setLeads(data || []);
        // Broker-only: derive upcoming renewals from insurance policies across broker's assigned schemes.
        if (isBroker) {
          const sids = Array.from(new Set((data || []).map((l) => l.scheme_id)));
          const now = new Date();
          const in90d = new Date(now.getTime() + 90 * 86400_000);
          const nextRenewals = [];
          for (const sid of sids) {
            try {
              const { data: pols } = await api.get(`/schemes/${sid}/insurance`);
              for (const p of pols || []) {
                const end = p.period_end && new Date(p.period_end);
                if (end && end >= now && end <= in90d) {
                  nextRenewals.push({ ...p, scheme_id: sid });
                }
              }
            } catch (_e) { /* scheme not visible → skip */ }
          }
          if (mounted) setRenewals(nextRenewals.sort((a, b) => (a.period_end || "").localeCompare(b.period_end || "")).slice(0, 5));
        }
      } catch (_e) { /* handled by SPShell */ }
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [isBroker]);

  const buckets = KANBAN.reduce((acc, col) => {
    acc[col.key] = leads.filter((l) => {
      if (col.key === "WON") return ["WON", "LOST", "CLOSED", "DECLINED"].includes(l.status);
      if (col.key === "QUOTE_SENT") return l.status === "QUOTE_SENT" || l.status === "QUOTED" || l.status === "RESPONDED";
      if (col.key === "ACKNOWLEDGED") return l.status === "ACKNOWLEDGED" || l.status === "VIEWED";
      return l.status === col.key;
    });
    return acc;
  }, {});

  if (loading) return <div className="h-40 rounded-xl border animate-pulse bg-slate-50" style={{ borderColor: "var(--border)" }} />;

  return (
    <div className="space-y-8" data-testid={isBroker ? "broker-cockpit" : "it-sp-cockpit"}>
      <div>
        <h2 className="text-lg font-semibold mb-4" style={{ color: "var(--navy)" }}>
          {isBroker ? "Open quotes to respond to" : "Open leads"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {KANBAN.map((col) => (
            <div key={col.key} className="rounded-xl border bg-slate-50/60 p-3 min-h-[240px]" style={{ borderColor: "var(--border)" }} data-testid={`sp-kanban-col-${col.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: col.bg, color: col.color }}>
                  {col.label}
                </span>
                <span className="text-xs font-semibold text-slate-500 tabular-nums">{buckets[col.key].length}</span>
              </div>
              <div className="space-y-2">
                {buckets[col.key].map((l) => {
                  const days = l.submitted_at
                    ? Math.max(0, Math.floor((Date.now() - new Date(l.submitted_at).getTime()) / 86400_000))
                    : null;
                  return (
                    <div key={l.lead_id} className="rounded-lg bg-white border p-3" style={{ borderColor: "var(--border)" }} data-testid={`sp-kanban-card-${l.lead_id}`}>
                      <div className="text-sm font-semibold" style={{ color: "var(--navy)" }}>{l.scheme_name || "Scheme"}</div>
                      {l.category && <div className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wider">{l.category.replace(/_/g, " ")}</div>}
                      {l.brief && <p className="text-xs text-slate-600 mt-2 leading-snug line-clamp-3">{l.brief}</p>}
                      {days !== null && <div className="mt-2 text-[11px] text-slate-500">{days === 0 ? "today" : `${days}d ago`}</div>}
                    </div>
                  );
                })}
                {buckets[col.key].length === 0 && (
                  <div className="text-xs text-slate-500 italic p-2">No leads</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {isBroker && (
        <div className="rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="broker-renewals-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            <TrendingUp className="w-3.5 h-3.5" /> Insurance renewals · next 90 days
          </div>
          {renewals.length === 0 ? (
            <div className="text-sm text-slate-500">No renewals in the next 90 days across your assigned schemes.</div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {renewals.map((r) => (
                <div key={r.policy_id} className="py-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <div>
                      <div className="text-sm font-semibold" style={{ color: "var(--navy)" }}>{r.insurer_name} · {r.policy_type}</div>
                      <div className="text-xs text-slate-500">Sum insured ${(r.sum_insured || 0).toLocaleString()} · renews {r.period_end ? new Date(r.period_end).toLocaleDateString() : "TBC"}</div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2 py-1 rounded" style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }}>
                    upsell signal
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
